import { useCallback, useMemo, useState } from "react";

export interface PendingSourceChange {
  documentId: string;
  documentName?: string;
  path?: string;
}

export function usePendingSourceChanges(): {
  pendingChanges: Record<string, PendingSourceChange>;
  hasPendingChanges: boolean;
  stageChange: (groupDocumentId: string, replacement: { id: string; name?: string }) => void;
  applyResolvedPath: (groupDocumentId: string, replacementDocumentId: string, path: string) => void;
  revertChange: (groupDocumentId: string) => void;
  clearPendingChanges: () => void;
} {
  const [pendingChanges, setPendingChanges] = useState<Record<string, PendingSourceChange>>({});

  const stageChange = useCallback((groupDocumentId: string, replacement: { id: string; name?: string }) => {
    setPendingChanges((current) => ({
      ...current,
      [groupDocumentId]: { documentId: replacement.id, documentName: replacement.name },
    }));
  }, []);

  const applyResolvedPath = useCallback((groupDocumentId: string, replacementDocumentId: string, path: string) => {
    setPendingChanges((current) => {
      const entry = current[groupDocumentId];
      if (!entry || entry.documentId !== replacementDocumentId) {
        return current;
      }
      return { ...current, [groupDocumentId]: { ...entry, path } };
    });
  }, []);

  const revertChange = useCallback((groupDocumentId: string) => {
    setPendingChanges((current) => {
      if (!(groupDocumentId in current)) {
        return current;
      }
      const next = { ...current };
      delete next[groupDocumentId];
      return next;
    });
  }, []);

  const clearPendingChanges = useCallback(() => {
    setPendingChanges({});
  }, []);

  const hasPendingChanges = useMemo(() => Object.keys(pendingChanges).length > 0, [pendingChanges]);

  return {
    pendingChanges,
    hasPendingChanges,
    stageChange,
    applyResolvedPath,
    revertChange,
    clearPendingChanges,
  };
}
